import { useMemo, useRef, useState, useEffect } from 'react';
import {
  FormNames,
  OptionalReportDocument,
  OptionalReportRequest,
  OptionalReportSchema,
} from '@youscience/commonapp-service-common';
import { CoreBox } from '@youscience/core';

import { FormState } from '@constants/commonApp/types';

import { TabSections } from '@interfaces/recommenderWorkspace';

import { useUserProfile } from '@hooks/useUserProfile';

import useRecommenderWorkspaceStore from '@store/RecommenderWorkspaceStore';
import useNotificationStore from '@store/NotificationStore';

import useRecommenderStore from '@store/RecommenderStore';

import OtherUpdates, { OtherUpdatesFields } from './components/OtherUpdates';
import FormHeader from '../../common/FormHeader';
import FormFooter from '../../common/FormFooter';
import ClassRank, { ClassRankFields } from './components/ClassRank';
import GPA, { GPAFields } from './components/GPA';
import Transcripts, { TranscriptsFields } from './components/Transcripts';

import { sxStyles } from './OptionalReport.styles';

import {
  checkCounselorProfileValidity,
  checkFormValidity,
  PREVIEW_ERROR_NOTIFICATION,
  REVIEW_NOT_READY_ERROR_NOTIFICATION,
  onSaveErrorNotification,
  onSaveSuccessNotification,
  onSubmitSuccessNotification,
  onUnsubmitSuccessNotification,
} from '../../common/formUtils';
import ReviewPdf, { ReviewTypes } from '../../common/ReviewPdf';
import Submissions from '../../common/Submissions';
import { useApplicantForm } from '@pages/StudentDetailsPage/hooks/useApplicantForm';
import { FormAvailabilityStatus, FormCompletionStatus } from '../../common/enums';
import { formatDateWithFallback } from '@utils/formatter';
import Summary, { SummaryFields } from './components/Summary';
import { useRemoveShowErrorOnUnmount, removeShowErrorsFromUrl } from '@utils/navigation';

enum SUMMARY_VALUES {
  transcripts = '0',
  classRank = '1',
  gpa = '2',
  enrolledCourses = '3',
  evaluation = '4',
  otherCorrection = '5',
}

interface OptionalReportFormState {
  [TabSections.Summary]: SummaryFields;
  [TabSections.ClassRank]: OptionalReportRequest[TabSections.ClassRank];
  [TabSections.GPA]: OptionalReportRequest[TabSections.GPA];
  [TabSections.Transcripts]: OptionalReportRequest[TabSections.Transcripts];
  [TabSections.OtherUpdates]: OptionalReportRequest[TabSections.OtherUpdates];
}

interface OptionalReportProps {
  formName: FormNames.OPTIONAL_REPORT | FormNames.OPTIONAL_REPORT2;
}

const getSummaryFields = (updates?: string[]): SummaryFields => {
  return {
    classRank: updates?.includes(SUMMARY_VALUES.classRank),
    gpa: updates?.includes(SUMMARY_VALUES.gpa),
    transcripts: updates?.includes(SUMMARY_VALUES.transcripts),
    enrolledCourses: updates?.includes(SUMMARY_VALUES.enrolledCourses),
    evaluation: updates?.includes(SUMMARY_VALUES.evaluation),
    otherCorrection: updates?.includes(SUMMARY_VALUES.otherCorrection),
  };
};

const getPreparedData = (
  formState: OptionalReportFormState,
  applicantId: number,
  recommenderId: number,
  section?: keyof OptionalReportFormState,
) => {
  const updates = [] as SUMMARY_VALUES[];

  Object.entries(formState[TabSections.Summary]).map(([key, value]) => {
    if (value) {
      updates.push(SUMMARY_VALUES[key as keyof SummaryFields]);
    }
  });

  const preparedData = {
    applicantId,
    recommenderId,
    [TabSections.Summary]: { updates },
    [TabSections.GPA]: updates.includes(SUMMARY_VALUES.gpa) ? formState[TabSections.GPA] : null,
    [TabSections.ClassRank]: updates.includes(SUMMARY_VALUES.classRank) ? formState[TabSections.ClassRank] : null,
    [TabSections.Transcripts]: updates.includes(SUMMARY_VALUES.transcripts) ? formState[TabSections.Transcripts] : null,
    [TabSections.OtherUpdates]:
      updates.includes(SUMMARY_VALUES.enrolledCourses) ||
      updates.includes(SUMMARY_VALUES.evaluation) ||
      updates.includes(SUMMARY_VALUES.otherCorrection)
        ? formState[TabSections.OtherUpdates]
        : null,
  };

  return section ? { applicantId, recommenderId, [section]: preparedData[section] } : preparedData;
};

const getShowSections = (summary: SummaryFields) => {
  return {
    showClassRank: summary.classRank,
    showGpa: summary.gpa,
    showTranscripts: summary.transcripts,
    showOtherUpdates: summary.enrolledCourses || summary.evaluation || summary.otherCorrection,
  };
};

export const OptionalReport = (props: OptionalReportProps) => {
  useRemoveShowErrorOnUnmount();

  const { formName } = props;
  const optionalReport2Value = formName === FormNames.OPTIONAL_REPORT2 ? '2' : '';
  const { notify } = useNotificationStore((state) => state);
  const { userId } = useUserProfile();
  const { recommenderProfile } = useRecommenderStore((store) => store);
  const optionalReport = useApplicantForm<OptionalReportDocument>(formName);
  const initialFormState = useMemo(
    () => ({
      [TabSections.Summary]: getSummaryFields(optionalReport.content?.summary?.updates),
      [TabSections.GPA]: optionalReport.content?.gpa,
      [TabSections.ClassRank]: optionalReport.content?.classRank,
      [TabSections.Transcripts]: optionalReport.content?.transcripts,
      [TabSections.OtherUpdates]: optionalReport.content?.otherUpdates,
    }),
    [optionalReport.content],
  );
  const form = useRef(initialFormState);
  const [formErrors, setFormErrors] = useState({});
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [reviewType, setReviewType] = useState<ReviewTypes>(ReviewTypes.PREVIEW_FORM);
  const [expandedSection, setExpandedSection] = useState<TabSections | null>(TabSections.ClassRank);
  const [showSection, setShowSection] = useState(getShowSections(initialFormState[TabSections.Summary]));
  const isShowErrors = new URLSearchParams(window.location.search).get('showErrors') === 'true';

  const isSubmitted =
    optionalReport.metadata?.completionStatus === FormCompletionStatus.SUBMITTED ||
    optionalReport.metadata?.completionStatus === FormCompletionStatus.DOWNLOADED;
  const isSubmitDisabled = optionalReport.metadata?.availabilityStatus === FormAvailabilityStatus.NOT_AVAILABLE;
  const { applicantId, recommenderId } = optionalReport.metadata;

  const toggleExpandedSelection = (section: TabSections) => {
    if (expandedSection === section) {
      setExpandedSection(null);
    } else {
      setExpandedSection(section);
    }
  };

  const onFormChangeHandler =
    <K,>(section: TabSections) =>
    (sectionFormState: FormState<K>) => {
      form.current = { ...form.current, [section]: sectionFormState.data };
      if (section === TabSections.Summary) {
        setShowSection(getShowSections(sectionFormState.data as SummaryFields));
      }
    };

  const validateForm = () => {
    const formValid = checkFormValidity(
      getPreparedData(form.current, applicantId, recommenderId),
      OptionalReportSchema,
    );
    const profileValid = recommenderProfile
      ? checkCounselorProfileValidity(recommenderProfile)
      : { personal: false, school: false };

    const summaryFields = form.current[TabSections.Summary];
    const hasAtLeastOneCheckbox = Object.values(summaryFields).some((value) => value === true);

    return {
      form: !formValid || !hasAtLeastOneCheckbox,
      recommenderProfile: !profileValid.personal,
      schoolProfile: !profileValid.school,
    };
  };

  const handleSaveForLater = async (options?: {
    isHideNotification?: boolean;
    section?: keyof typeof initialFormState;
  }) => {
    const { isHideNotification, section } = options || {};
    const preparedData = getPreparedData(form.current, applicantId, recommenderId, section);
    await optionalReport.save({ ...preparedData, applicantId, recommenderId });

    if (!isHideNotification) {
      optionalReport.isError ? onSaveErrorNotification() : onSaveSuccessNotification(formName);
    }

    if (section && !optionalReport.isError) {
      toggleExpandedSelection(section);
      const nextSection = getNextSection(section);
      if (nextSection) toggleExpandedSelection(nextSection);
    }
  };

  const previewForm = async () => {
    if (isSubmitted) {
      setIsPreviewOpen(true);
      await optionalReport.getSubmitted({ applicantId, recommenderId });
    } else {
      await handleSaveForLater({ isHideNotification: true });

      const formErrors = validateForm();
      setFormErrors(formErrors);
      setIsPreviewOpen(true);
      const hasFormErrors = Object.values(formErrors).some(Boolean);

      if (!hasFormErrors) {
        removeShowErrorsFromUrl();
        await optionalReport.preview(getPreparedData(form.current, applicantId, recommenderId));
      }
    }

    useRecommenderWorkspaceStore.subscribe((state) => {
      if (state.isError) {
        notify(isSubmitted ? REVIEW_NOT_READY_ERROR_NOTIFICATION : PREVIEW_ERROR_NOTIFICATION);
        setIsPreviewOpen(false);
      }
    });
  };

  const submitForm = async (signature: string, signedDate: string) => {
    await optionalReport.submit({
      applicantId,
      recommenderId,
      counselorApproval: { isReviewed: true, isAffirmed: true, signature, signedDate, userId: userId as string },
    });

    optionalReport.isError ? onSaveErrorNotification() : onSubmitSuccessNotification(formName);
  };

  const handleOnSubmit = () => {
    setReviewType(ReviewTypes.REVIEW_AND_SUBMIT);
    void previewForm();
  };

  const handleOnPreview = () => {
    setReviewType(isSubmitted ? ReviewTypes.REVIEW_FORM : ReviewTypes.PREVIEW_FORM);
    void previewForm();
  };

  const getNextSection = (section: TabSections): TabSections | undefined => {
    switch (section) {
      case TabSections.Summary:
        if (form.current[TabSections.Summary].classRank) return TabSections.ClassRank;
        if (form.current[TabSections.Summary].gpa) return TabSections.GPA;
        if (form.current[TabSections.Summary].transcripts) return TabSections.Transcripts;
        if (
          form.current[TabSections.Summary].enrolledCourses ||
          form.current[TabSections.Summary].evaluation ||
          form.current[TabSections.Summary].otherCorrection
        )
          return TabSections.OtherUpdates;
        break;
      case TabSections.ClassRank:
        if (form.current[TabSections.Summary].gpa) return TabSections.GPA;
        if (form.current[TabSections.Summary].transcripts) return TabSections.Transcripts;
        if (
          form.current[TabSections.Summary].enrolledCourses ||
          form.current[TabSections.Summary].evaluation ||
          form.current[TabSections.Summary].otherCorrection
        )
          return TabSections.OtherUpdates;
        break;
      case TabSections.GPA:
        if (form.current[TabSections.Summary].transcripts) return TabSections.Transcripts;
        if (
          form.current[TabSections.Summary].enrolledCourses ||
          form.current[TabSections.Summary].evaluation ||
          form.current[TabSections.Summary].otherCorrection
        )
          return TabSections.OtherUpdates;
        break;
      case TabSections.Transcripts:
        if (
          form.current[TabSections.Summary].enrolledCourses ||
          form.current[TabSections.Summary].evaluation ||
          form.current[TabSections.Summary].otherCorrection
        )
          return TabSections.OtherUpdates;
        break;
      default:
        return undefined;
    }
  };

  const handleOnUnsubmit = async () => {
    await optionalReport.unsubmit({ applicantId, recommenderId });

    optionalReport.isError ? onSaveErrorNotification() : onUnsubmitSuccessNotification(formName);
  };

  useEffect(() => {
    if (isShowErrors && optionalReport.content) {
      const errors = validateForm();
      setFormErrors(errors);

      const sections = [
        TabSections.Summary,
        ...(showSection.showClassRank ? [TabSections.ClassRank] : []),
        ...(showSection.showGpa ? [TabSections.GPA] : []),
        ...(showSection.showTranscripts ? [TabSections.Transcripts] : []),
        ...(showSection.showOtherUpdates ? [TabSections.OtherUpdates] : []),
      ] as const;

      const summaryHasNoSelection = !Object.values(form.current[TabSections.Summary]).some((value) => value === true);
      if (summaryHasNoSelection) {
        setExpandedSection(TabSections.Summary);
        return;
      }

      const fullValidation = OptionalReportSchema.safeParse(getPreparedData(form.current, applicantId, recommenderId));

      if (!fullValidation.success) {
        const firstErrorSection = sections.find((section) => {
          return fullValidation.error.issues.some((issue) => issue.path[0] === section);
        });

        if (firstErrorSection) {
          setExpandedSection(firstErrorSection);
          return;
        }
      }

      if (errors.form) {
        const firstVisibleErrorSection = sections.find((section) => {
          if (!(section in form.current)) return false;
          const sectionData = form.current[section as keyof OptionalReportFormState];
          if (!sectionData) return false;

          try {
            const sectionFormState = { [section]: sectionData };
            checkFormValidity(sectionFormState, OptionalReportSchema);
            return false;
          } catch (error) {
            return true;
          }
        });

        if (firstVisibleErrorSection) {
          setExpandedSection(firstVisibleErrorSection);
        }
      }
    }
  }, [isShowErrors, optionalReport.content, showSection, applicantId, recommenderId]);

  if (!optionalReport.isReady) {
    return null;
  }

  return (
    <CoreBox sx={sxStyles.pageWrap}>
      <FormHeader
        name={`Optional report ${optionalReport2Value}`}
        tooltipTitle={`You may use this form to update grades or transcripts outside of the mid-year report and final report. You can also use the optional report ${optionalReport2Value} to correct errors in other submitted forms.`}
        deadline={formatDateWithFallback(optionalReport.metadata?.deadline)}
        status={optionalReport.metadata.completionStatus || FormCompletionStatus.NEW}
        onPreviewClick={handleOnPreview}
        onSaveForLaterClick={handleSaveForLater}
        onSubmitClick={handleOnSubmit}
        onUnsubmitClick={handleOnUnsubmit}
        areAllActionsDisabled={optionalReport.isSavingForm}
        isSubmitDisabled={isSubmitDisabled}
        isFormSumbitted={isSubmitted}
        formName={formName}
      />

      {isSubmitted ? (
        <Submissions formName={formName} onSubmit={handleOnSubmit} />
      ) : (
        <>
          <Summary
            summary={getSummaryFields(optionalReport.content?.summary?.updates)}
            isExtended={expandedSection === TabSections.Summary}
            onExpand={() => toggleExpandedSelection(TabSections.Summary)}
            onFormChange={onFormChangeHandler<SummaryFields>(TabSections.Summary)}
            isSaved={optionalReport.sectionIsSaved.summary}
            onContinue={
              getNextSection(TabSections.Summary)
                ? () => handleSaveForLater({ section: TabSections.Summary })
                : undefined
            }
            showError={
              Object.values(formErrors).some(Boolean) &&
              !Object.values(form.current[TabSections.Summary]).some((value) => value === true)
            }
          />

          {showSection.showClassRank ? (
            <ClassRank
              classRank={optionalReport.content?.classRank || recommenderProfile?.schoolProfile?.classRank}
              isExtended={expandedSection === TabSections.ClassRank}
              onExpand={() => toggleExpandedSelection(TabSections.ClassRank)}
              onFormChange={onFormChangeHandler<ClassRankFields>(TabSections.ClassRank)}
              isSaved={optionalReport.sectionIsSaved.classRank}
              onContinue={
                getNextSection(TabSections.ClassRank)
                  ? () => handleSaveForLater({ section: TabSections.ClassRank })
                  : undefined
              }
              showError={
                Object.values(formErrors).some(Boolean) &&
                !!form.current[TabSections.ClassRank] &&
                !Object.values(form.current[TabSections.ClassRank] || {}).some((value) => !!value)
              }
            />
          ) : null}

          {showSection.showGpa ? (
            <GPA
              gpa={optionalReport.content?.gpa || recommenderProfile?.schoolProfile?.gpa}
              isExtended={expandedSection === TabSections.GPA}
              onExpand={() => toggleExpandedSelection(TabSections.GPA)}
              onContinue={
                getNextSection(TabSections.GPA)
                  ? () => {
                      handleSaveForLater({ section: TabSections.GPA });
                    }
                  : undefined
              }
              onFormChange={onFormChangeHandler<GPAFields>(TabSections.GPA)}
              isSaved={optionalReport.sectionIsSaved.gpa}
              showError={
                Object.values(formErrors).some(Boolean) &&
                !!form.current[TabSections.GPA] &&
                !Object.values(form.current[TabSections.GPA] || {}).some((value) => !!value)
              }
            />
          ) : null}

          {showSection.showTranscripts ? (
            <Transcripts
              transcripts={
                optionalReport.content?.transcripts ||
                (recommenderProfile?.schoolProfile?.transcripts as TranscriptsFields)
              }
              isExtended={expandedSection === TabSections.Transcripts}
              onExpand={() => toggleExpandedSelection(TabSections.Transcripts)}
              onContinue={
                getNextSection(TabSections.Transcripts)
                  ? () => handleSaveForLater({ section: TabSections.Transcripts })
                  : undefined
              }
              onFormChange={onFormChangeHandler<TranscriptsFields>(TabSections.Transcripts)}
              isSaved={optionalReport.sectionIsSaved.transcripts}
              applicantName={optionalReport.metadata.studentName}
              showError={
                Object.values(formErrors).some(Boolean) &&
                !!form.current[TabSections.Transcripts] &&
                !Object.values(form.current[TabSections.Transcripts] || {}).some((value) => !!value)
              }
            />
          ) : null}

          {showSection.showOtherUpdates ? (
            <OtherUpdates
              otherUpdates={optionalReport.content?.otherUpdates}
              isExtended={expandedSection === TabSections.OtherUpdates}
              onExpand={() => toggleExpandedSelection(TabSections.OtherUpdates)}
              onFormChange={onFormChangeHandler<OtherUpdatesFields>(TabSections.OtherUpdates)}
              isSaved={optionalReport.sectionIsSaved.otherUpdates}
              applicantName={optionalReport.metadata.studentName}
              showError={
                Object.values(formErrors).some(Boolean) &&
                !!form.current[TabSections.OtherUpdates] &&
                !form.current[TabSections.OtherUpdates]?.changeExplanationOriginalFiles?.length
              }
            />
          ) : null}
        </>
      )}

      <FormFooter
        onPreviewClick={handleOnPreview}
        onSaveForLaterClick={handleSaveForLater}
        onSubmitClick={handleOnSubmit}
        onUnsubmitClick={handleOnUnsubmit}
        areAllActionsDisabled={optionalReport.isSavingForm}
        isSubmitDisabled={isSubmitDisabled}
        isFormSumbitted={isSubmitted}
        formName={formName}
      />

      {isPreviewOpen ? (
        <ReviewPdf
          title={`Optional Report ${optionalReport2Value}`}
          subtitle="Please review the PDF preview of your form. Please scroll to the bottom if necessary to submit."
          isOpen={isPreviewOpen}
          isLoading={optionalReport.isLoadingPreview}
          reviewType={reviewType}
          pdf={optionalReport.previewPdfURL}
          onClose={() => setIsPreviewOpen(false)}
          onContinue={(signature?: string, date?: string) => {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            if (reviewType === ReviewTypes.REVIEW_AND_SUBMIT) submitForm(signature!, date!);
          }}
          errors={formErrors}
        />
      ) : null}
    </CoreBox>
  );
};
